import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import CommentIcon from '../../assets/images/Web_Comments_Icon.svg';
import resources from '../../resources.json';

/**
 * Optimized CommentComponent for displaying comments section
 * @param {Object} props - Component props
 * @param {string} props.commentsList - Comments content to display
 * @returns {JSX.Element} Rendered comment component
 */
const CommentComponent = memo(({ commentsList }) => {
    // Memoize the comment header content to avoid re-creating on each render
    const headerContent = useMemo(() => (
        <>
            <img 
                src={CommentIcon} 
                className="CommentIcon" 
                alt="Comment Icon"
                loading="lazy"
            />
            {resources.CommentSection}
        </>
    ), []);

    // Memoize component styles to prevent re-creation
    const componentStyles = useMemo(() => ({
        commentsection: {
            display: 'flex',
            flexDirection: 'column'
        },
        commentheader: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        commentbody: {
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
        }
    }), []);

    // Early return for empty comments
    if (!commentsList) {
        return null;
    }

    return (
        <div 
            className="commentsection"
            style={componentStyles.commentsection}
            role="region"
            aria-label="Comments section"
        >
            <div 
                className="commentheader"
                style={componentStyles.commentheader}
            >
                {headerContent}
            </div>
            <div 
                className="commentbody"
                style={componentStyles.commentbody}
            >
                <span>{commentsList}</span>
            </div>
        </div>
    );
});

// Display name for debugging
CommentComponent.displayName = 'CommentComponent';

// PropTypes for type checking
CommentComponent.propTypes = {
    commentsList: PropTypes.string
};

// Default props
CommentComponent.defaultProps = {
    commentsList: ''
};

export default CommentComponent;
